import {useCallback, useMemo} from 'react';
import {assert} from 'assert-ts';
import {Concept} from 'types';
import type {HttpError} from 'api/http/types';
import {useAppDispatch} from 'store/hooks';
import type {
  ChangeRequest,
  EntityContainer,
  EntitySaveStatus,
  ExpressionV4,
  ManifestationV4,
  MetadataSaveOperations,
  OptionalSaveStatus,
  SaveStatus,
  WorkV4,
} from '../types';
import {
  useCreateNewExpressionWithManifestationMutation,
  useSaveExpressionMutation,
} from '../expressionApi';
import {removeChangeRequestInLocalStore} from '../localStorage';
import {
  useSaveManifestationMutation,
  useTriggerManifestationOutboxMutation,
} from '../manifestationApi';
import {
  metadataEditChangeRequestSaved,
  metadataEditExpressionSaved,
  metadataEditManifestationSaved,
  metadataEditNewExpressionWithManifestationSaved,
  metadataEditSaved,
  metadataEditSetSaving,
  metadataEditWorkSaved,
} from '../metadataEditSlice';
import {useSaveWorkMutation} from '../workApi';
import {useCloseChangeRequest} from './useCloseChangeRequest';
import {useMetadataSchemasCodelists} from './useMetadataSchemasCodelists';
import {useTriggerStatementOfResponsibilityGeneratedFeedback} from './useTriggerStatementOfResponsibilityGeneratedFeedback';

export const useMetadataSaveOperations = (): MetadataSaveOperations => {
  const closeChangeRequest = useCloseChangeRequest();
  const [triggerManifestationOutboxMutation] =
    useTriggerManifestationOutboxMutation();
  const triggerStatementOfResponsibilityGeneratedFeedback =
    useTriggerStatementOfResponsibilityGeneratedFeedback();
  const [saveExpressionMutation] = useSaveExpressionMutation();
  const [createNewExpressionWithManifestationMutation] =
    useCreateNewExpressionWithManifestationMutation();
  const [saveManifestationMutation] = useSaveManifestationMutation();
  const [saveWorkMutation] = useSaveWorkMutation();

  const dispatch = useAppDispatch();

  const codelists = useMetadataSchemasCodelists();

  const saveWork = useCallback(
    (work: WorkV4): Promise<SaveStatus> => {
      return saveWorkMutation(work)
        .unwrap()
        .then(updatedWork => {
          dispatch(metadataEditWorkSaved(updatedWork));

          return {status: 'Saved'} as SaveStatus;
        })
        .catch((error: HttpError) => {
          return {status: 'Failed' as const, error};
        });
    },
    [saveWorkMutation, dispatch],
  );

  const saveExpression = useCallback(
    (expression: ExpressionV4): Promise<SaveStatus> => {
      return saveExpressionMutation(expression)
        .unwrap()
        .then(updatedExpression => {
          dispatch(metadataEditExpressionSaved(updatedExpression));

          return {status: 'Saved'} as SaveStatus;
        })
        .catch((error: HttpError) => {
          return {status: 'Failed' as const, error};
        });
    },
    [dispatch, saveExpressionMutation],
  );

  const saveManifestation = useCallback(
    (manifestation: ManifestationV4): Promise<SaveStatus> => {
      return saveManifestationMutation(manifestation)
        .unwrap()
        .then(response => {
          dispatch(metadataEditManifestationSaved(response));

          // Notify user if field has been populated from backend
          triggerStatementOfResponsibilityGeneratedFeedback(
            manifestation,
            response.manifestation,
          );

          return {status: 'Saved'} as SaveStatus;
        })
        .catch((error: HttpError) => {
          return {status: 'Failed' as const, error};
        });
    },
    [
      saveManifestationMutation,
      dispatch,
      triggerStatementOfResponsibilityGeneratedFeedback,
    ],
  );

  /**
   * saveChangeRequest, i.e. update status if from backend, remove if from local, then
   * dispatch SAVED with status 'COMPLETED'
   */
  const saveChangeRequest = useCallback(
    (
      changeRequest: ChangeRequest,
      onlyChangeRequests: boolean,
    ): Promise<SaveStatus> => {
      return (
        changeRequest.storage === 'backend' // Backend: Update status in backend
          ? closeChangeRequest(changeRequest.id, changeRequest.taskType)
          : changeRequest.storage === 'local'
            ? // Local: Remove from local storage
              Promise.resolve(
                removeChangeRequestInLocalStore(
                  changeRequest.id,
                  changeRequest.work.id,
                ),
              )
            : // Transient: do nothing
              Promise.resolve()
      )
        .then(() => {
          const updatedChangeRequest: ChangeRequest = {
            ...changeRequest,
            status: 'COMPLETED',
            work: {
              id: changeRequest.work.id,
            } as WorkV4,
            expression: {
              id: changeRequest.expression.id,
              workId: changeRequest.expression.workId,
            } as ExpressionV4,
            manifestation: {
              id: changeRequest.manifestation.id,
              expressionId: changeRequest.manifestation.expressionId,
            } as ManifestationV4,
          };

          // If only change requests are being processed, trigger manifestation outbox BEFORE dispatching
          // the SAVED action to avoid race condition where editStatuses are cleared before
          // subsequent save operations can determine what entities need to be saved.
          if (onlyChangeRequests) {
            // Meta needs us to trigger outbox when change request is closed with no changes to metadata.
            return triggerManifestationOutboxMutation(
              changeRequest.manifestation,
            ).then(() => {
              dispatch(metadataEditChangeRequestSaved(updatedChangeRequest));

              return {status: 'Saved'} as SaveStatus;
            });
          } else {
            dispatch(metadataEditChangeRequestSaved(updatedChangeRequest));

            return {status: 'Saved'} as SaveStatus;
          }
        })
        .catch((error: HttpError) => {
          return {status: 'Failed' as const, error};
        });
    },
    [closeChangeRequest, dispatch, triggerManifestationOutboxMutation],
  );

  const saveMetadataEntities = useCallback(
    async (entities: EntityContainer[]): Promise<EntitySaveStatus[]> => {
      if (
        !assert.soft(entities.length > 0, 'saveMetadataByIds: no ids to save')
      ) {
        return [];
      }

      dispatch(metadataEditSetSaving({saving: true}));

      const result = entities.map<EntitySaveStatus>(e => ({
        entity: {type: e.type, id: e.data.id},
        status: {status: 'None'},
      }));

      const onlyChangeRequests = entities.every(
        e => e.type === Concept.changeRequest,
      );

      for (let i = 0; i < entities.length; i++) {
        const entity = entities[i];
        const status = await (entity.type === Concept.work
          ? saveWork(entity.data)
          : entity.type === Concept.expression
            ? saveExpression(entity.data)
            : entity.type === Concept.manifestation
              ? saveManifestation(entity.data)
              : saveChangeRequest(entity.data, onlyChangeRequests));

        result[i].status = status;
        if (status.status === 'Failed') {
          break;
        }
      }

      // Dispatch SAVED action if all successful
      if (result.every(r => r.status.status === 'Saved')) {
        dispatch(metadataEditSaved({codelists}));
      }

      if (result.every(r => r.status.status !== 'None')) {
        dispatch(metadataEditSetSaving({saving: false}));
      }

      return result;
    },
    [
      dispatch,
      saveWork,
      saveExpression,
      saveManifestation,
      saveChangeRequest,
      codelists,
    ],
  );

  /** Save one or more of work, expression or manifestation to backend and update reducer state if successful */
  const saveNewExpressionWithManifestation = useCallback(
    (
      expression: ExpressionV4,
      manifestation: ManifestationV4,
    ): Promise<OptionalSaveStatus> => {
      const localExpressionId = expression.id;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      delete expression['id'];
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      delete manifestation['expressionId'];

      return createNewExpressionWithManifestationMutation({
        expression,
        manifestation,
      })
        .unwrap()
        .then(
          ({
            expression: updatedExpression,
            manifestation: updatedManifestation,
          }) => {
            dispatch(
              metadataEditNewExpressionWithManifestationSaved({
                localExpressionId,
                expression: updatedExpression,
                manifestation: updatedManifestation,
                codelists,
              }),
            );

            return {status: 'Saved' as const};
          },
        )
        .catch((error: HttpError) => {
          return {status: 'Failed' as const, error};
        });
    },
    [createNewExpressionWithManifestationMutation, dispatch, codelists],
  );

  return useMemo(() => {
    return {
      saveWork,
      saveExpression,
      saveManifestation,
      saveMetadataEntities,
      saveNewExpressionWithManifestation,
      saveChangeRequest,
    };
  }, [
    saveChangeRequest,
    saveExpression,
    saveManifestation,
    saveMetadataEntities,
    saveNewExpressionWithManifestation,
    saveWork,
  ]);
};
